/**
 * Small element builders shared by every module that draws. Each returns a
 * plain element made with `document.createElement` and `textContent`, never
 * `innerHTML`, so a value can never be read as markup.
 */

/** Makes an element, with an optional class name. */
export const createElement = <TagName extends keyof HTMLElementTagNameMap>(
  tagName: TagName,
  className?: string,
): HTMLElementTagNameMap[TagName] => {
  const element = document.createElement(tagName)
  if (className !== undefined) {
    element.className = className
  }
  return element
}

/** Makes an element that holds one piece of text. */
export const createTextElement = <TagName extends keyof HTMLElementTagNameMap>(
  tagName: TagName,
  text: string,
  className?: string,
): HTMLElementTagNameMap[TagName] => {
  const element = createElement(tagName, className)
  element.textContent = text
  return element
}

/** Makes a link. An address that starts with `http` opens in a new tab, with no opener. */
export const createLink = (href: string, text: string, className?: string): HTMLAnchorElement => {
  const link = createTextElement('a', text, className)
  link.href = href
  if (href.startsWith('http')) {
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
  }
  return link
}
